import React, {RefObject} from "react";
import ReactMarkdown from "react-markdown";
import "./Announcement.scss";
import {getTranslate, Translate, TranslateFunction} from "react-localize-redux";
import {Button, Modal} from "antd";
import {connect} from "react-redux";
import {Nullable} from "@data-force/app";
import {AnnouncementData} from "../../interfaces/store/actions/payload/home";
import {DefaultAnnouncementIcon} from "../../assets/Icons";
import StoreState from "../../interfaces/store/storeState";
import {SetFocusedElementAction} from "../../interfaces/store/actions/focusElement";
import {setFocusedElement} from "../../store/actions/focusElement";

interface OwnProps {
    data: AnnouncementData
}

interface StateProps {
    translate: TranslateFunction
    focusedElement: Nullable<RefObject<HTMLElement>>
}

interface DispatchProps {
    setFocusedElement(payload: Nullable<RefObject<HTMLElement>>): SetFocusedElementAction
}

type Props = StateProps & OwnProps & DispatchProps;

class Announcement extends React.Component<Props> {
    private static readonly translationPath: string = "components.Announcement.Announcement";

    private learnMoreRef: RefObject<HTMLButtonElement> = React.createRef();

    constructor(props: Props) {
        super(props);

        this.onCloseHandler = this.onCloseHandler.bind(this);
        this.showFullAnnouncement = this.showFullAnnouncement.bind(this);
        this.onFocusChange = this.onFocusChange.bind(this);
    }

    /**
     * When element is in focus, calls action which sets that reference in store as currently focused element
     */
    onFocusChange(): void {
        this.props.setFocusedElement(this.learnMoreRef);
    }

    /**
     * Closing modal for full announcements and restoring focus on last 'learn more' button
     */
    onCloseHandler(): void {
        const {focusedElement}: Props = this.props;
        if (focusedElement) {
            (focusedElement as RefObject<HTMLElement>).current?.focus();
        }
    }

    /**
     * Show modal widow with full provided content
     */
    showFullAnnouncement(): void {
        const {info} = Modal;
        const {data, translate}: Props = this.props;
        info({
            className: "df-announcement-modal",
            title: data.Headline,
            content: <ReactMarkdown linkTarget="_blank">{data.Content}</ReactMarkdown>,
            centered: true,
            maskClosable: true,
            closable: true,
            okText: translate(`${Announcement.translationPath}.okBtnTxt.text`),
            width: 572,
            onOk: this.onCloseHandler,
            onCancel: this.onCloseHandler,
        });
    }

    render(): React.ReactElement {
        const {data}: Props = this.props;
        return (
            <div className="df-announcement-item">
                <div className="df-announcement-img-container">
                    {data.Picture ? <img src={data.Picture} alt=""/> : <DefaultAnnouncementIcon/>}
                </div>
                <div className="df-announcement-content">
                    <h3 className="df-announcement-headline">{data.Headline}</h3>
                    <span className="df-announcement-date">
                        {data.Date}
                    </span>
                    <ReactMarkdown linkTarget="_blank">{data.Introduction}</ReactMarkdown>
                    {data.Content && (
                        <Button
                            type="link"
                            className="df-announcement-more"
                            onClick={this.showFullAnnouncement}
                            onFocus={this.onFocusChange}
                            ref={this.learnMoreRef}
                        >
                            <Translate id={`${Announcement.translationPath}.learnMore.text`}/>
                        </Button>
                    )}
                </div>
            </div>
        );
    }
}

function mapStateToProps(state: StoreState): StateProps {
    return {
        translate: getTranslate(state.localize),
        focusedElement: state.focusElement.focusedElement,
    };
}

const mapDispatchToProps: DispatchProps = {
    setFocusedElement,
};

export default connect(mapStateToProps, mapDispatchToProps)(Announcement);
